import React, { useState, useContext, useEffect, useRef } from 'react';
import ButtonV2 from 'components/common/ButtonV2';
import { axiosGetAPI, axiosUpdate, axiosPost } from 'services';
import { authContext } from 'contexts/AuthContext';
import { toast } from 'react-toastify';
import { calculateDistanceAndTransit } from './distanceService';

const WEB_REQUEST_STATIC_REMARKS = 'Transporter se confirm ho gaya';

/* ------------------------------------------------------------------ */
/* Shared styles                                                       */
/* ------------------------------------------------------------------ */
const headerStyle = {
  padding: '10px 14px',
  textAlign: 'left',
  fontWeight: 700,
  color: '#344054',
  background: '#f2f4f7',
  borderBottom: '2px solid #e4e7ec',
  borderRight: '1px solid #e4e7ec',
  fontSize: '13px',
  whiteSpace: 'nowrap',
};

const cellStyle = {
  padding: '12px 14px',
  color: '#344054',
  verticalAlign: 'middle',
  fontSize: '13px',
  borderBottom: '1px solid #e9ecef',
  borderRight: '1px solid #e9ecef',
};

const inputStyle = {
  height: '34px',
  border: '1px solid #d0d5dd',
  borderRadius: '6px',
  padding: '0 10px',
  fontSize: '13px',
  color: '#101828',
  width: '100%',
  outline: 'none',
  background: '#fff',
};

const STATUS_STYLE = {
  Pending: { background: '#fafafa', color: '#475467', border: '1px solid #d0d5dd' },
  Approved: { background: '#eff4ff', color: '#1d4ed8', border: '1px solid #c7d7fe' },
  Rejected: { background: '#f2f4f7', color: '#344054', border: '1px solid #d0d5dd' },
};

const extractRouteRows = (res) => {
  const payload = res?.data || res;
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const findMatchingRoute = (rows, originId, destId, originName, destName) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  if (originId && destId) {
    const matchById = rows.find(
      (r) =>
        String(r.originCityId ?? r.originCity?.id ?? r.originId ?? '') === String(originId) &&
        String(r.destinationCityId ?? r.destinationCity?.id ?? r.destinationId ?? '') === String(destId)
    );
    if (matchById) return matchById;
  }

  if (originName && destName) {
    const cleanOrigin = String(originName).trim().toLowerCase();
    const cleanDest = String(destName).trim().toLowerCase();
    const matchByName = rows.find(
      (r) =>
        String(r.originCityName ?? r.originCity?.cityName ?? '').trim().toLowerCase() === cleanOrigin &&
        String(r.destinationCityName ?? r.destinationCity?.cityName ?? '').trim().toLowerCase() === cleanDest
    );
    if (matchByName) return matchByName;
  }

  return null;
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
const RequestDetailView = ({ request, source = 'admin' }) => {
  const { auth } = useContext(authContext);
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);

  const [routeId, setRouteId] = useState(
    request?.routeId ||
    request?.originalReq?.routeId ||
    request?.originalReq?.deliveryRouteId ||
    request?.originalReq?.route?.id ||
    null
  );
  const [originCityId, setOriginCityId] = useState(
    request?.originCityId ||
    request?.originalReq?.originCityId ||
    request?.originalReq?.originCity?.id ||
    request?.originalReq?.originId ||
    null
  );
  const [destinationCityId, setDestinationCityId] = useState(
    request?.destinationCityId ||
    request?.originalReq?.destinationCityId ||
    request?.originalReq?.destinationCity?.id ||
    request?.originalReq?.destinationId ||
    null
  );

  const [distance, setDistance] = useState('');
  const [transitTime, setTransitTime] = useState('');
  const [rowData, setRowData] = useState({});
  const [webRates, setWebRates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [transitTimeError, setTransitTimeError] = useState('');
  const transitTimeInputRef = useRef(null);

  // Dedicated states for Request Web single row
  const [webQuantity, setWebQuantity] = useState(
    request?.originalReq?.quantity ?? request?.quantity ?? ''
  );
  const [webQuantityUnit, setWebQuantityUnit] = useState(
    request?.originalReq?.actualQuantityUnit || request?.quantityUnit || 'MT'
  );
  const [webStatus, setWebStatus] = useState(
    String(request?.status || 'PENDING').toUpperCase() === 'APPROVED' ? 'APPROVED' : 'PENDING'
  );
  const [webRate, setWebRate] = useState(
    String(request?.perKgRate ?? '')
  );
  const [webAmount, setWebAmount] = useState(
    String(request?.ftlRate ?? '')
  );

  useEffect(() => {
    let isMounted = true;
    const fetchVehiclesAndRequest = async () => {
      try {
        setLoadingVehicles(true);

        if (source === 'web') {
          const reqEndpoint = `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/web-request/${request.id}`;
          const [res, vehiclesRes] = await Promise.all([
            axiosGetAPI(reqEndpoint, auth),
            axiosGetAPI(`${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/vehicle-sizes`, auth),
          ]);
          if (isMounted) {
            const raw = res?.data?.data || res?.data || request;
            const vehicleSizes = Array.isArray(vehiclesRes?.data?.data) ? vehiclesRes.data.data : [];
            const apiVehicles = vehicleSizes.map((vehicle) => {
              const quantityMatch = String(vehicle.tonnage ?? vehicle.label ?? vehicle.value ?? '')
                .match(/\d+(?:\.\d+)?/);
              const tonnage = quantityMatch ? Number(quantityMatch[0]) : NaN;
              return {
                id: vehicle.value,
                name: vehicle.label || `${tonnage} MT`,
                capacity: Number.isFinite(tonnage) ? `${tonnage} Ton` : '-',
                tonnage,
              };
            }).filter((vehicle) => Number.isFinite(vehicle.tonnage));
            setVehicles(apiVehicles);

            if (raw) {
              if (raw.quantity != null) setWebQuantity(raw.quantity);
              if (raw.actualQuantityUnit || raw.quantityUnit) setWebQuantityUnit(raw.actualQuantityUnit || raw.quantityUnit);
              if (raw.status) setWebStatus(String(raw.status).toUpperCase() === 'APPROVED' ? 'APPROVED' : 'PENDING');
              if (raw.distanceKm != null) setDistance(String(raw.distanceKm));
              if (raw.transitTimeDays) setTransitTime(String(raw.transitTimeDays));
              if (raw.perKgRate != null) setWebRate(String(raw.perKgRate));
              if (raw.ftlRate != null) setWebAmount(String(raw.ftlRate));
              const savedRates = (Array.isArray(raw.rates) ? raw.rates : []).map((rate) => ({
                id: rate.id,
                quantity: rate.quantity == null ? '' : String(rate.quantity),
                perKgRate: rate.perKgRate == null ? '' : String(rate.perKgRate),
                totalAmount: rate.totalAmount ?? rate.ftlRate ?? '',
                status: String(rate.status || 'PENDING').toUpperCase() === 'APPROVED' ? 'APPROVED' : 'PENDING',
              }));
              const vehicleRates = apiVehicles.map((vehicle) => {
                const savedRate = savedRates.find((rate) => Number(rate.quantity) === vehicle.tonnage);
                return savedRate
                  ? { ...savedRate, vehicleName: vehicle.name, capacity: vehicle.capacity }
                  : {
                    quantity: String(vehicle.tonnage),
                    perKgRate: '',
                    totalAmount: '',
                    status: 'PENDING',
                    vehicleName: vehicle.name,
                    capacity: vehicle.capacity,
                  };
              });
              const unmatchedRates = savedRates
                .filter((rate) => !apiVehicles.some((vehicle) => Number(rate.quantity) === vehicle.tonnage))
                .map((rate) => ({
                  ...rate,
                  vehicleName: `${rate.quantity} ${raw.actualQuantityUnit || raw.quantityUnit || 'MT'}`,
                  capacity: '-',
                }));
              setWebRates([...vehicleRates, ...unmatchedRates]);

              if (!(Number(raw.distanceKm) > 0) && (raw.origin || request?.origin) && (raw.destination || request?.destination)) {
                try {
                  const oName = request?.origin || raw.origin;
                  const dName = request?.destination || raw.destination;
                  const distRes = await calculateDistanceAndTransit(oName, dName);
                  if (distRes?.distanceKm) setDistance(String(distRes.distanceKm));
                  if (distRes?.transitTime) setTransitTime(String(distRes.transitTime));
                } catch (e) {
                  // ignore
                }
              }
            }
            setLoadingVehicles(false);
          }
          return;
        }

        const [vehiclesRes, requestRes] = await Promise.all([
          axiosGetAPI(`${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/vehicle-sizes`, auth),
          axiosGetAPI(`${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/request/${request.id}`, auth)
        ]);

        if (isMounted && vehiclesRes?.isSuccessful && vehiclesRes?.data?.data) {
          const apiVehicles = vehiclesRes.data.data.map((v) => ({
            id: v.value,
            name: v.label,
            capacity: `${v.tonnage} Ton`,
          }));
          setVehicles(apiVehicles);

          let currentDist = '';
          let currentTransit = '';
          const rawData = requestRes?.data?.data;
          const nestedRoute = rawData?.route || request?.originalReq?.route;

          const reqOrigin = request?.origin || rawData?.originCityName;
          const reqDest = request?.destination || rawData?.destinationCityName;

          // Verify nestedRoute strictly matches this request's origin and destination
          let validRoute = null;
          if (nestedRoute) {
            const originMatch = !reqOrigin ||
              String(nestedRoute?.originCityName ?? nestedRoute?.originCity?.cityName ?? '').trim().toLowerCase() === String(reqOrigin).trim().toLowerCase();
            const destMatch = !reqDest ||
              String(nestedRoute?.destinationCityName ?? nestedRoute?.destinationCity?.cityName ?? '').trim().toLowerCase() === String(reqDest).trim().toLowerCase();

            if (originMatch && destMatch) {
              validRoute = nestedRoute;
            }
          }

          let rId =
            routeId ||
            validRoute?.id ||
            rawData?.routeId ||
            rawData?.deliveryRouteId ||
            rawData?.route_id ||
            request?.routeId ||
            request?.originalReq?.routeId ||
            request?.originalReq?.route?.id ||
            null;

          const oCityId =
            rawData?.originCityId ??
            validRoute?.originCityId ??
            request?.originCityId ??
            request?.originalReq?.originCityId ??
            request?.originalReq?.originCity?.id ??
            request?.originalReq?.originId ??
            originCityId;
          if (oCityId) setOriginCityId(oCityId);

          const dCityId =
            rawData?.destinationCityId ??
            validRoute?.destinationCityId ??
            request?.destinationCityId ??
            request?.originalReq?.destinationCityId ??
            request?.originalReq?.destinationCity?.id ??
            request?.originalReq?.destinationId ??
            destinationCityId;
          if (dCityId) setDestinationCityId(dCityId);

          let routeFromApi = validRoute || null;

          if (rId && !routeFromApi) {
            try {
              const singleRouteRes = await axiosGetAPI(
                `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/route/${rId}`,
                auth
              );
              if (singleRouteRes?.isSuccessful && singleRouteRes?.data?.data) {
                const fetched = singleRouteRes.data.data;
                const originMatch = !reqOrigin ||
                  String(fetched?.originCityName ?? fetched?.originCity?.cityName ?? '').trim().toLowerCase() === String(reqOrigin).trim().toLowerCase();
                const destMatch = !reqDest ||
                  String(fetched?.destinationCityName ?? fetched?.destinationCity?.cityName ?? '').trim().toLowerCase() === String(reqDest).trim().toLowerCase();

                if (originMatch && destMatch) {
                  routeFromApi = fetched;
                }
              }
            } catch (e) {
              console.error('Error fetching route by id:', e);
            }
          }

          if (!routeFromApi && (oCityId || reqOrigin) && (dCityId || reqDest)) {
            try {
              const apiBaseUrl =
                process.env.REACT_APP_API_BASE_URL ||
                process.env.REACT_APP_REST_API_ENDPOINT?.replace(/\/$/, '');
              let queryParam = 'pageNumber=0&pageSize=100';
              if (oCityId) queryParam += `&originCityId=${oCityId}`;
              if (dCityId) queryParam += `&destinationCityId=${dCityId}`;
              const routesRes = await axiosGetAPI(
                `${apiBaseUrl}/api/delivery-rate/v2/routes?${queryParam}`,
                auth
              );
              const foundRows = extractRouteRows(routesRes);
              const matchingRoute = findMatchingRoute(foundRows, oCityId, dCityId, reqOrigin, reqDest);
              if (matchingRoute?.id || matchingRoute?.routeId) {
                rId = matchingRoute.id || matchingRoute.routeId;
                routeFromApi = matchingRoute;
              }
            } catch (e) {
              console.error('Error prefetching route ID:', e);
            }
          }

          if (routeFromApi?.id || routeFromApi?.routeId) {
            setRouteId(routeFromApi.id || routeFromApi.routeId);
          }

          // Distance: Use backend distance if present; otherwise automatically autofetch
          const distVal =
            routeFromApi?.distanceKm ??
            rawData?.distanceKm ??
            request?.originalReq?.distanceKm;

          if (distVal !== null && distVal !== undefined && distVal !== '' && Number(distVal) > 0) {
            currentDist = String(distVal);
          } else if (reqOrigin && reqDest) {
            try {
              const calculated = await calculateDistanceAndTransit(reqOrigin, reqDest);
              if (calculated?.distanceKm) {
                currentDist = String(calculated.distanceKm);
              }
            } catch (err) {
              console.warn('Auto-fetch distance failed on load:', err);
            }
          }

          // Transit time: Only use if already present in backend; NEVER autofill
          const transitVal =
            routeFromApi?.transitTimeDays ??
            rawData?.transitTimeDays ??
            request?.originalReq?.transitTimeDays;

          if (transitVal !== null && transitVal !== undefined && transitVal !== '') {
            currentTransit = String(transitVal);
          }

          if (isMounted) {
            setDistance(currentDist);
            setTransitTime(currentTransit);
          }

          // Vehicle rates: only populate if saved for this specific route/request
          const ratesSource = routeFromApi || rawData;

          setRowData(
            Object.fromEntries(
              apiVehicles.map((v) => {
                const num = v.id.replace('MT_', '');
                const perKgKey = `perKg${num}Mt`;
                const ftlKey = `ftl${num}Mt`;
                let rateVal = '';
                let amountVal = '';

                const rawRate = ratesSource?.[perKgKey];
                if (rawRate !== undefined && rawRate !== null && rawRate !== '') {
                  rateVal = String(rawRate);
                }

                const rawFtl = ratesSource?.[ftlKey];
                if (rawFtl !== undefined && rawFtl !== null && rawFtl !== '') {
                  amountVal = String(rawFtl);
                } else if (rateVal) {
                  const tonnage = parseInt(num, 10) || 10;
                  amountVal = String(Math.round(parseFloat(rateVal) * tonnage * 1000));
                }

                return [
                  v.id,
                  {
                    amount: amountVal,
                    rate: rateVal,
                    submitted: !!rateVal,
                    status: rateVal
                      ? 'Approved'
                      : (v.name === request.vehicle ? (request.status || 'Pending') : 'Pending'),
                  },
                ];
              })
            )
          );
        }
      } catch (err) {
        console.error("Error fetching vehicles and request details:", err);
      } finally {
        if (isMounted) setLoadingVehicles(false);
      }
    };
    fetchVehiclesAndRequest();
    return () => { isMounted = false; };
  }, [auth, request]);

  // Entering Rate multiplies by tonnage * 1000 to calculate TotalAmount
  const handleRateChange = (vehicleId, value) => {
    setRowData((prev) => {
      const tonnage = parseInt(vehicleId.replace(/\D/g, ''), 10) || 10;
      const multiplier = tonnage * 1000;
      const calculatedAmount =
        value !== '' && !Number.isNaN(Number(value)) && multiplier
          ? Math.round(parseFloat(value) * multiplier)
          : '';

      return {
        ...prev,
        [vehicleId]: {
          ...prev[vehicleId],
          rate: value,
          amount: String(calculatedAmount),
        },
      };
    });
  };

  const getWebAmount = () => {
    const quantity = Number(webQuantity);
    const rate = Number(webRate);
    if (!Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(rate) || rate <= 0) {
      return String(webAmount ?? '');
    }
    const unit = String(webQuantityUnit || 'MT').trim().toUpperCase();
    const quantityInKg = ['MT', 'TON', 'TONNE'].includes(unit) ? quantity * 1000 : quantity;
    return String(Math.round(rate * quantityInKg * 100) / 100);
  };

  const getWebTierAmount = (rate) => {
    const quantity = Number(rate?.quantity);
    const perKgRate = Number(rate?.perKgRate);
    if (!Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(perKgRate) || perKgRate <= 0) {
      return String(rate?.totalAmount ?? '');
    }

    const unit = String(webQuantityUnit || 'MT').trim().toUpperCase();
    const quantityInKg = ['MT', 'TON', 'TONNE'].includes(unit) ? quantity * 1000 : quantity;
    return String(Math.round(perKgRate * quantityInKg * 100) / 100);
  };

  const updateWebRate = (index, key, value) => {
    setWebRates((current) => current.map((rate, rateIndex) => (
      rateIndex === index
        ? { ...rate, [key]: value, ...(key === 'perKgRate' ? { totalAmount: '' } : {}) }
        : rate
    )));
  };

  const getDistanceValue = () =>
    parseFloat(distance) ||
    (distance && String(distance).match(/\d+(\.\d+)?/) ? parseFloat(String(distance).match(/\d+(\.\d+)?/)[0]) : null) ||
    parseFloat(request?.originalReq?.distanceKm) ||
    parseFloat(request?.originalReq?.route?.distanceKm) ||
    600.0;

  const getTransitTimeValue = () =>
    (transitTime && String(transitTime).trim()) ||
    (request?.originalReq?.transitTimeDays && String(request.originalReq.transitTimeDays).trim()) ||
    (request?.originalReq?.route?.transitTimeDays && String(request.originalReq.route.transitTimeDays).trim()) ||
    '';

  const getRemarksValue = () =>
    (request?.originalReq?.remarks && String(request.originalReq.remarks).trim()) ||
    "Transporter se confirm ho gaya";

  const hasValidRate = (row) => (
    row &&
    row.rate !== '' &&
    row.rate !== null &&
    row.rate !== undefined &&
    !Number.isNaN(Number(row.rate)) &&
    parseFloat(row.rate) > 0
  );

  // Update rates (works for single entry or multiple entries)
  const submitRates = async () => {
    if (source !== 'web' && !String(transitTime).trim()) {
      setTransitTimeError('Please fill this field');
      requestAnimationFrame(() => {
        transitTimeInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        transitTimeInputRef.current?.focus();
      });
      return;
    }

    const targetRequestId = request?.id || request?.originalReq?.id;
    if (!targetRequestId) {
      toast.error("Rate request ID not found to update.");
      return;
    }

    if (source === 'web') {
      const hasPrimaryRate = webRate !== '' && Number.isFinite(Number(webRate)) && Number(webRate) > 0;
      const hasTierRate = webRates.some(
        (rate) => rate.perKgRate !== '' && Number.isFinite(Number(rate.perKgRate)) && Number(rate.perKgRate) > 0
      );
      if (!hasPrimaryRate && !hasTierRate) {
        toast.error('Please enter a rate for at least one quantity.');
        return;
      }

      setSubmitting(true);
      try {
        const payload = {
          remarks: WEB_REQUEST_STATIC_REMARKS,
          rates: webRates
            .filter((rate) => (
              rate.id ||
              (rate.perKgRate !== '' && Number.isFinite(Number(rate.perKgRate)) && Number(rate.perKgRate) > 0)
            ))
            .map((rate) => {
            const amount = getWebTierAmount(rate);
            return {
              quantity: Number(rate.quantity),
              ftlRate: amount !== '' && Number.isFinite(Number(amount)) ? Number(amount) : null,
              perKgRate: rate.perKgRate !== '' && Number.isFinite(Number(rate.perKgRate))
                ? Number(rate.perKgRate)
                : null,
              totalAmount: amount !== '' && Number.isFinite(Number(amount)) ? Number(amount) : null,
              status: rate.status || 'PENDING',
            };
          }),
        };

        // Keep supporting the original requested-quantity rate when it is filled.
        if (hasPrimaryRate) {
          payload.status = webStatus || 'PENDING';
          payload.perKgRate = Number(webRate);
          const primaryAmount = getWebAmount();
          if (primaryAmount !== '' && Number.isFinite(Number(primaryAmount))) {
            payload.ftlRate = Number(primaryAmount);
          }
        }
        if (distance !== '' && Number.isFinite(Number(distance))) payload.distanceKm = Number(distance);
        if (String(transitTime).trim()) payload.transitTimeDays = String(transitTime).trim();

        const requestUrl = `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/web-request/${targetRequestId}`;
        const res = await axiosUpdate(requestUrl, payload, auth?.data?.token);

        if (res?.isSuccessful) {
          const successMsg = res?.data?.message || "Rate request updated successfully.";
          toast.success(successMsg);
          const savedRates = res?.data?.data?.rates;
          if (Array.isArray(savedRates)) {
            setWebRates(savedRates.map((rate) => ({
              id: rate.id,
              quantity: rate.quantity == null ? '' : String(rate.quantity),
              perKgRate: rate.perKgRate == null ? '' : String(rate.perKgRate),
              totalAmount: rate.totalAmount ?? rate.ftlRate ?? '',
              status: String(rate.status || 'PENDING').toUpperCase() === 'APPROVED' ? 'APPROVED' : 'PENDING',
            })));
          }
        } else {
          const errMsg = res?.message || res?.data?.message || "Failed to update rate request.";
          toast.error(errMsg);
        }
      } catch (err) {
        console.error("Error updating rate request:", err);
        toast.error("An error occurred while updating rate request.");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const hasAnyRate = Object.values(rowData).some((r) => r.rate && parseFloat(r.rate) > 0);
    if (!hasAnyRate) {
      toast.error("Please enter rate for at least one vehicle.");
      return;
    }

    setSubmitting(true);

    try {
      // A request is approved only when its requested vehicle has a rate.
      // Rates entered for other vehicle sizes are saved without approving it.
      const requestedVehicleTonnage = String(request?.vehicle || '').match(/\d+(?:\.\d+)?/)?.[0];
      const requestedVehicleRow = requestedVehicleTonnage
        ? rowData[`MT_${requestedVehicleTonnage}`] ||
          rowData[requestedVehicleTonnage] ||
          Object.entries(rowData).find(([key]) => (
            key.replace(/\D/g, '') === requestedVehicleTonnage.replace(/\D/g, '')
          ))?.[1]
        : null;
      const isRequestedVehicleRated = hasValidRate(requestedVehicleRow);

      const payload = {
        action: isRequestedVehicleRated ? "APPROVE" : "SAVE",
        distanceKm: getDistanceValue(),
        transitTimeDays: getTransitTimeValue(),
      };

      const tonnages = ['10', '15', '20', '25', '30', '35'];
      if (Array.isArray(vehicles)) {
        vehicles.forEach((v) => {
          const num = v.id ? String(v.id).replace(/\D/g, '') : '';
          if (num && !tonnages.includes(num)) {
            tonnages.push(num);
          }
        });
      }

      tonnages.forEach((num) => {
        const ftlKey = `ftl${num}Mt`;
        const perKgKey = `perKg${num}Mt`;
        const row =
          rowData[`MT_${num}`] ||
          rowData[num] ||
          Object.entries(rowData).find(([k]) => k.replace(/\D/g, '') === num)?.[1];

        const hasRate = hasValidRate(row);

        if (hasRate) {
          const rateVal = parseFloat(row.rate);
          let amountVal = null;
          if (row.amount !== '' && row.amount !== null && row.amount !== undefined && !Number.isNaN(Number(row.amount))) {
            amountVal = parseFloat(row.amount);
          } else {
            const tonInt = parseInt(num, 10) || 10;
            amountVal = Math.round(rateVal * tonInt * 1000);
          }
          payload[ftlKey] = amountVal;
          payload[perKgKey] = rateVal;
        }
      });

      payload.remarks = getRemarksValue();

      if (request?.isStatic) {
        toast.success('Rate request updated successfully (Web Demo).');
        setRowData((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((id) => {
            if (next[id].rate && parseFloat(next[id].rate) > 0) {
              next[id] = { ...next[id], submitted: true, status: 'Approved' };
            }
          });
          return next;
        });
        setSubmitting(false);
        return;
      }

      // Update rate request via PUT /delivery-rate/v2/request/{id}
      const requestUrl = `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/request/${targetRequestId}`;
      const res = await axiosUpdate(requestUrl, payload, auth?.data?.token);

      if (res?.isSuccessful) {
        const successMsg = res?.data?.message || "Rate request updated successfully.";
        toast.success(successMsg);

        const updatedRoute = res?.data?.data?.route;
        if (updatedRoute?.id) {
          setRouteId(updatedRoute.id);
        }

        setRowData((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((id) => {
            if (next[id].rate && parseFloat(next[id].rate) > 0) {
              next[id] = { ...next[id], submitted: true, status: 'Approved' };
            }
          });
          return next;
        });
      } else {
        const errMsg = res?.message || res?.data?.message || "Failed to update rate request.";
        toast.error(errMsg);
      }
    } catch (err) {
      console.error("Error updating rate request:", err);
      toast.error("An error occurred while updating rate request.");
    } finally {
      setSubmitting(false);
    }
  };

  let submitButtonLabel = 'Update All';
  if (source === 'web') {
    submitButtonLabel = webRates.length > 0 ? 'Update Rates' : 'Update';
  }
  if (submitting) {
    submitButtonLabel = 'Updating...';
  }

  return (
    <div className="mx-wrap">

      {/* Origin / Destination / Distance / Transit Time */}
      <div className="sr-filter-card">
        <div className="mx-filter-row">

          <div className="mx-field">
            <label className="sr-label" htmlFor="rrd-origin">Origin</label>
            <input
              id="rrd-origin"
              type="text"
              className="sr-select"
              style={{ height: '42px', backgroundColor: '#fff', cursor: 'default' }}
              value={request?.origin || ''}
              readOnly
            />
          </div>

          <div className="mx-field">
            <label className="sr-label" htmlFor="rrd-dest">Destination</label>
            <input
              id="rrd-dest"
              type="text"
              className="sr-select"
              style={{ height: '42px', backgroundColor: '#fff', cursor: 'default' }}
              value={request?.destination || ''}
              readOnly
            />
          </div>

          <div className="mx-field">
            <label className="sr-label" htmlFor="rrd-distance">Distance (km)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                id="rrd-distance"
                type="number"
                min="0"
                placeholder="e.g. 1400"
                className="sr-select"
                style={{ height: '42px' }}
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
              />
              <span style={{ color: '#667085', fontSize: '13px', whiteSpace: 'nowrap' }}>km</span>
            </div>
          </div>

          <div className="mx-field" style={{ position: 'relative' }}>
            <label className="sr-label" htmlFor="rrd-transit">Transit Time (Days)</label>
            <input
              ref={transitTimeInputRef}
              id="rrd-transit"
              type="text"
              placeholder="e.g. 5-6"
              className="sr-select"
              style={{
                height: '42px',
                borderColor: transitTimeError ? '#d92d20' : undefined,
              }}
              value={transitTime}
              onChange={(e) => {
                setTransitTime(e.target.value);
                if (transitTimeError) setTransitTimeError('');
              }}
            />
            {transitTimeError && (
              <span style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px',
                color: '#d92d20',
                fontSize: '12px',
              }}>
                {transitTimeError}
              </span>
            )}
          </div>

        </div>
      </div>

      {/* All vehicles rate table */}
      <div className="push-notification-container" style={{ padding: 0, gap: 0 }}>

        {/* Title */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #e4e7ec',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#1d2939' }}>
            {source === 'web' ? 'Enter Rate' : 'Enter Rate per Vehicle'}
          </span>
          <span style={{ fontSize: '12px', color: '#667085' }}>{source === 'web' ? '₹ / KG' : '₹ / kg'}</span>
        </div>

        {/* Table */}
        <div className="push-notification__table-wrap" style={{ borderRadius: 0, border: 0 }}>
          <table className="push-notification__table" style={{ minWidth: source === 'web' ? 850 : 620 }}>
            <thead>
              {source === 'web' ? (
                <tr>
                  <th style={{ ...headerStyle, width: 50 }}>S.No.</th>
                  <th style={headerStyle}>Vehicle Type</th>
                  <th style={{ ...headerStyle, width: 110 }}>Capacity</th>
                  <th style={{ ...headerStyle, width: 140 }}>Status</th>
                  <th style={{ ...headerStyle, width: 180 }}>Rate+Loading (₹/KG)</th>
                  <th style={{ ...headerStyle, width: 180 }}>TotalAmount (₹)</th>
                </tr>
              ) : (
                <tr>
                  <th style={{ ...headerStyle, width: 50 }}>S.No.</th>
                  <th style={headerStyle}>Vehicle Type</th>
                  <th style={{ ...headerStyle, width: 110 }}>Capacity</th>
                  <th style={{ ...headerStyle, width: 110 }}>Status</th>
                  <th style={{ ...headerStyle, width: 180 }}>Rate+Loading (₹/kg)</th>
                  <th style={{ ...headerStyle, width: 180 }}>TotalAmount (₹)</th>
                </tr>
              )}
            </thead>
            <tbody>
              {loadingVehicles ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#667085' }}>
                    {source === 'web' ? 'Loading request details...' : 'Loading vehicle types...'}
                  </td>
                </tr>
              ) : source === 'web' ? (
                <>
                <tr style={{ background: '#fff' }}>
                  {/* S.No. */}
                  <td style={{ ...cellStyle, color: '#667085', textAlign: 'center' }}>
                    1
                  </td>

                  {/* Quantity */}
                  <td style={{ ...cellStyle, fontWeight: 600, color: '#1d2939' }}>
                    <span className="sr-trip-vehicle">
                      {webQuantity === '' ? '-' : `${webQuantity} ${webQuantityUnit || 'MT'}`}
                    </span>
                    <span style={{
                      marginLeft: 8, fontSize: '10px', fontWeight: 700, color: '#1d4ed8',
                      background: '#eff4ff', border: '1px solid #c7d7fe', borderRadius: '999px', padding: '2px 8px',
                    }}>
                      Requested
                    </span>
                  </td>

                  <td style={{ ...cellStyle, textAlign: 'center' }}>-</td>

                  {/* Status dropdown */}
                  <td style={cellStyle}>
                    <select
                      value={webStatus}
                      onChange={(e) => setWebStatus(e.target.value)}
                      className="sr-select"
                      style={{
                        height: '34px',
                        fontSize: '12px',
                        fontWeight: 700,
                        borderRadius: '6px',
                        borderColor: '#d0d5dd',
                        padding: '2px 8px',
                        cursor: 'pointer',
                        backgroundColor: webStatus === 'APPROVED' ? '#ecfdf3' : '#fffaeb',
                        color: webStatus === 'APPROVED' ? '#027a48' : '#b54708',
                        outline: 'none',
                      }}
                    >
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                    </select>
                  </td>

                  {/* Rate+Loading input (editable) */}
                  <td style={cellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ color: '#667085', fontSize: '13px' }}>₹</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="e.g. 10.50"
                        aria-label="Rate"
                        style={{
                          ...inputStyle,
                          background: '#fff',
                        }}
                        value={webRate}
                        onChange={(e) => {
                          setWebRate(e.target.value);
                          if (e.target.value === '') setWebAmount('');
                        }}
                      />
                      <span style={{ color: '#667085', fontSize: '12px', whiteSpace: 'nowrap' }}>/KG</span>
                    </div>
                  </td>

                  {/* Total amount is calculated from the requested quantity. */}
                  <td style={cellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ color: '#667085', fontSize: '13px' }}>₹</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="e.g. 3100"
                        aria-label="Total Amount"
                        style={{
                          ...inputStyle,
                          background: '#fff',
                        }}
                        value={getWebAmount()}
                        readOnly
                      />
                    </div>
                  </td>
                </tr>
                {webRates.map((rate, idx) => {
                  const rowStatus = String(rate.status || 'PENDING').toUpperCase() === 'APPROVED'
                    ? 'APPROVED'
                    : 'PENDING';
                  const amount = getWebTierAmount(rate);
                  return (
                    <tr key={rate.id || `${rate.quantity}-${idx}`} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ ...cellStyle, color: '#667085', textAlign: 'center' }}>{idx + 2}</td>
                      <td style={{ ...cellStyle, fontWeight: 600, color: '#1d2939' }}>
                        <span className="sr-trip-vehicle">
                          {rate.vehicleName || (rate.quantity === '' ? '-' : `${rate.quantity} ${webQuantityUnit || 'MT'}`)}
                        </span>
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center' }}>
                        {rate.capacity === '-' ? '-' : (
                          <span className="sr-trip-vehicle" style={{ fontSize: '12px' }}>{rate.capacity || `${rate.quantity} Ton`}</span>
                        )}
                      </td>
                      <td style={cellStyle}>
                        <select
                          value={rowStatus}
                          onChange={(event) => updateWebRate(idx, 'status', event.target.value)}
                          className="sr-select"
                          aria-label={`Status for ${rate.quantity} ${webQuantityUnit || 'MT'}`}
                          style={{
                            height: '34px', fontSize: '12px', fontWeight: 700, borderRadius: '6px',
                            borderColor: '#d0d5dd', padding: '2px 8px', cursor: 'pointer',
                            backgroundColor: rowStatus === 'APPROVED' ? '#ecfdf3' : '#fffaeb',
                            color: rowStatus === 'APPROVED' ? '#027a48' : '#b54708', outline: 'none',
                          }}
                        >
                          <option value="PENDING">Pending</option>
                          <option value="APPROVED">Approved</option>
                        </select>
                      </td>
                      <td style={cellStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ color: '#667085', fontSize: '13px' }}>₹</span>
                          <input
                            type="number" min="0" step="0.01" placeholder="e.g. 10.50"
                            aria-label={`Rate for ${rate.quantity} ${webQuantityUnit || 'MT'}`}
                            style={{ ...inputStyle, background: '#fff' }}
                            value={rate.perKgRate}
                            onChange={(event) => updateWebRate(idx, 'perKgRate', event.target.value)}
                          />
                          <span style={{ color: '#667085', fontSize: '12px', whiteSpace: 'nowrap' }}>/KG</span>
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ color: '#667085', fontSize: '13px' }}>₹</span>
                          <input
                            type="number" min="0" placeholder="Auto"
                            aria-label={`Total amount for ${rate.quantity} ${webQuantityUnit || 'MT'}`}
                            style={{ ...inputStyle, background: '#f8fafc' }}
                            value={amount}
                            readOnly
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                </>
              ) : (
                vehicles.map((vehicle, idx) => {
                  const row = rowData[vehicle.id];
                  if (!row) return null;
                  const isRequested = vehicle.name === request.vehicle;
                  let rowBackground = idx % 2 === 0 ? '#fff' : '#fafafa';
                  if (isRequested) {
                    rowBackground = '#f5f8ff';
                  }

                  return (
                    <tr
                      key={vehicle.id}
                      style={{
                        background: rowBackground,
                      }}
                    >
                      {/* S.No. */}
                      <td style={{ ...cellStyle, color: '#667085', textAlign: 'center' }}>
                        {idx + 1}
                      </td>

                      {/* Vehicle name — highlight requested one */}
                      <td style={{ ...cellStyle, fontWeight: 600, color: '#1d2939' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {vehicle.name}
                          {isRequested && (
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 700,
                              background: '#eff4ff',
                              color: '#1d4ed8',
                              border: '1px solid #c7d7fe',
                              borderRadius: '999px',
                              padding: '2px 8px',
                            }}>
                              Requested
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Capacity */}
                      <td style={cellStyle}>
                        <span className="sr-trip-vehicle" style={{ fontSize: '12px' }}>
                          {vehicle.capacity}
                        </span>
                      </td>

                      {/* Status */}
                      <td style={cellStyle}>
                        <span style={{
                          display: 'inline-block',
                          borderRadius: '999px',
                          padding: '3px 10px',
                          fontSize: '11px',
                          fontWeight: 700,
                          ...(STATUS_STYLE[row.submitted ? 'Approved' : row.status] || STATUS_STYLE.Pending),
                        }}>
                          {row.submitted ? 'Approved' : row.status}
                        </span>
                      </td>

                      {/* Rate+Loading input (editable, triggers amount multiplication) */}
                      <td style={cellStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ color: '#667085', fontSize: '13px' }}>₹</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="e.g. 10.50"
                            aria-label={`Rate for ${vehicle.name}`}
                            style={{
                              ...inputStyle,
                              background: '#fff',
                            }}
                            value={row.rate || ''}
                            onChange={(e) => handleRateChange(vehicle.id, e.target.value)}
                          />
                          <span style={{ color: '#667085', fontSize: '12px', whiteSpace: 'nowrap' }}>/kg</span>
                        </div>
                      </td>

                      {/* TotalAmount (auto-calculated from rate * tonnage * 1000, readonly) */}
                      <td style={cellStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ color: '#667085', fontSize: '13px' }}>₹</span>
                          <input
                            type="number"
                            min="0"
                            placeholder="Auto"
                            aria-label={`Amount for ${vehicle.name}`}
                            style={{
                              ...inputStyle,
                              background: '#f8fafc',
                            }}
                            value={row.amount || ''}
                            readOnly
                          />
                        </div>
                      </td>

                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer for Submit All */}
        <div style={{ padding: '16px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e4e7ec' }}>
          <ButtonV2
            color="primary"
            style={{ height: '40px', padding: '0 24px', fontSize: '14px', fontWeight: 700, borderRadius: '8px' }}
            disabled={
              submitting ||
              (source === 'web'
                ? (!(webRate && parseFloat(webRate) > 0) && !webRates.some((rate) => rate.perKgRate && parseFloat(rate.perKgRate) > 0))
                : !Object.values(rowData).some(r => r.rate && parseFloat(r.rate) > 0))
            }
            onClick={() => submitRates()}
          >
            {submitButtonLabel}
          </ButtonV2>
        </div>

      </div>
    </div>
  );
};

export default RequestDetailView;
